import { getArticleMetaFromXML } from '../utilities/ArticleData';
import { isEmptyAuthorValue } from '../utilities/AuthorUtilities';
import { getRouterPathToArticle } from '../utilities/Links';
import {
  ARTICLE_LANGUAGE,
  ArticleLanguage,
  ArticleListItem,
  CategoryMeta,
  ContentType,
  CONTENT_TYPE,
} from './types';

type ArticleCategories = {
  primary: string;
  secondary: string[];
};

interface ArticleMetaInterface {
  id: string;
  title: string;
  releaseDate: string;
  publishDate: string;
  authors: string[];
  type: ContentType;
  isPublic: boolean;
  categories: CategoryMeta;
  language: ArticleLanguage;
  keywords: string[];
  isUpcoming: boolean;
  featuredDate?: string;
  description?: string;
  coverImage?: string;
  videoPlaylistId?: string;
  allCategories: () => string[];
  getRoute: () => string;
  hasCoverImage: () => boolean;
  hasVideoContent: () => boolean;
  isFeatured: () => boolean;
}

export default class ArticleMeta implements ArticleMetaInterface {
  id: string;
  title: string;
  releaseDate: string;
  publishDate: string;
  authors: string[];
  type: ContentType;
  isPublic: boolean;
  categories: CategoryMeta;
  language: ArticleLanguage;
  keywords: string[];
  isUpcoming: boolean;
  featuredDate?: string;
  description?: string;
  coverImage?: string;
  videoPlayListId?: string;

  constructor(
    id: string,
    title: string,
    // Date when the article was originally released in the JAA-service API
    releaseDate: string,
    // Date when article was last updated in the JAA-service API
    publishDate: string,
    authors: string[],
    type: ContentType,
    categories: ArticleCategories,
    language: ArticleLanguage,
    videoPlayListId?: string,
  ) {
    this.id = id;
    this.title = title;
    this.description = undefined;
    this.publishDate = publishDate;
    this.releaseDate = releaseDate;
    this.type = type;
    this.isPublic = false;
    this.authors = authors.filter((author) => !isEmptyAuthorValue(author));
    this.categories = categories;
    this.language = language;
    this.keywords = [];
    this.isUpcoming = false;
    this.videoPlayListId = videoPlayListId;

    // @NOTE: This will be implemented later
    this.coverImage = undefined;
  }

  public static fromApiArticleListItem(
    articleListItem: ArticleListItem,
  ): ArticleMeta {
    let title: string | undefined = undefined;
    const authors: string[] = [];
    let publishDate: string | undefined = undefined;
    let releaseDate: string | undefined = undefined;
    let categoryPrimary: string | undefined = undefined;
    const categorySecondary: string[] = [];
    let type = undefined;
    let isPublic = false;
    let coverImage = undefined;
    let language = undefined;
    let keywords: string[] = [];
    let isUpcoming = false;
    let description = undefined;
    let featuredDate = undefined;
    let videoPlayListId: string | undefined = undefined;

    articleListItem.metaData.forEach((metaData) => {
      switch (metaData.key) {
        case 'title':
          title = metaData.value;
          break;
        case 'author':
          authors.push(metaData.value);
          break;
        case 'publishDate':
          publishDate = metaData.value;
          break;
        case 'releaseDate':
          releaseDate = metaData.value;
          break;
        case 'categoryPrimary':
          categoryPrimary = metaData.value;
          break;
        case 'categorySecondary':
          categorySecondary.push(metaData.value);
          break;
        case 'contentType':
          type = ArticleMeta.getContentTypeFromString(metaData.value);

          if (type === undefined) {
            throw new Error('Unimplemented content type: ' + metaData.value);
          }

          break;
        case 'isPublic':
          isPublic = Boolean(metaData.value === '1');
          break;
        case 'upcoming':
          isUpcoming = Boolean(metaData.value === '1');
          break;
        case 'coverImage':
          coverImage = metaData.value;
          break;
        case 'language':
          language = metaData.value.toLowerCase() as ArticleLanguage;
          break;
        case 'keywords':
          keywords = ArticleMeta.getKeywordsFromString(metaData.value);
          break;
        case 'lead':
          description = metaData.value;
          break;
        case 'featureDocument':
          featuredDate = metaData.value;
          break;
        case 'videoplayerId':
          videoPlayListId = metaData.value;
          break;
        default:
          break;
      }
    });

    if (
      title === undefined ||
      type === undefined ||
      language === undefined ||
      publishDate === undefined ||
      categoryPrimary === undefined
    ) {
      throw new Error(
        'Missing one or more metadata: title, type, language, publishDate, releaseDate, categoryPrimary',
      );
    }

    const article = new ArticleMeta(
      articleListItem.id,
      title,
      releaseDate ?? publishDate,
      publishDate,
      authors,
      type,
      { primary: categoryPrimary, secondary: categorySecondary },
      language,
      videoPlayListId,
    );

    article.setPublic(isPublic);
    article.setKeywords(keywords);
    article.setUpcoming(isUpcoming);

    if (description !== undefined) {
      article.setDescription(description);
    }

    if (coverImage !== undefined) {
      article.setCoverImage(coverImage);
    }

    if (featuredDate !== undefined) {
      article.setFeaturedDate(featuredDate);
    }

    return article;
  }

  public static fromXML(id: string, articleXML: string) {
    const articleMeta = getArticleMetaFromXML(articleXML);
    const contentType =
      ArticleMeta.getContentTypeFromString(articleMeta.type) ??
      CONTENT_TYPE.Article;
    const language = ArticleMeta.getLanguageFrom(articleMeta.language);

    const article = new ArticleMeta(
      id,
      articleMeta.title,
      articleMeta.releaseDate ?? articleMeta.publishDate,
      articleMeta.publishDate,
      articleMeta.authors,
      contentType,
      articleMeta.categories,
      language,
      articleMeta.videoPlayListId,
    );

    article.setPublic(articleMeta.isPublic === '1');
    article.setKeywords(
      ArticleMeta.getKeywordsFromString(articleMeta.keywords ?? ''),
    );
    article.setUpcoming(articleMeta.upcoming === '1');

    if (articleMeta.description !== undefined) {
      article.setDescription(articleMeta.description);
    }

    if (articleMeta.coverImage !== undefined) {
      article.setCoverImage(articleMeta.coverImage);
    }

    return article;
  }

  setCoverImage(src?: string): void {
    if (src === undefined) {
      this.coverImage = undefined;
      return;
    }

    this.coverImage = src;
  }

  setDescription(description?: string): void {
    this.description = description;
  }

  setFeaturedDate(date?: string): void {
    this.featuredDate = date;
  }

  setPublic(isPublic: boolean) {
    this.isPublic = isPublic;
  }

  setKeywords(keywords: string[]): void {
    this.keywords = keywords;
  }

  setUpcoming(isUpcoming: boolean) {
    this.isUpcoming = isUpcoming;
  }

  allCategories(): string[] {
    return [this.categories.primary, ...this.categories.secondary];
  }

  getRoute(): string {
    return getRouterPathToArticle(this.id);
  }

  hasCoverImage(): boolean {
    return !!this.coverImage;
  }

  hasVideoContent(): boolean {
    return !!this.videoPlayListId;
  }

  isFeatured(): boolean {
    return !!this.featuredDate;
  }

  private static getContentTypeFromString(
    value: string,
  ): ContentType | undefined {
    const contentTypes: ContentType[] = Object.values(CONTENT_TYPE);

    if (contentTypes.includes(value as ContentType)) {
      return value as ContentType;
    }

    return undefined;
  }

  private static getLanguageFrom(value?: string): ArticleLanguage {
    const articleLanguages: ArticleLanguage[] = Object.values(ARTICLE_LANGUAGE);
    if (articleLanguages.includes(value as ArticleLanguage)) {
      return value as ArticleLanguage;
    }

    return ARTICLE_LANGUAGE.FI as ArticleLanguage;
  }

  private static getKeywordsFromString(keywordsString: string): string[] {
    const keywords = keywordsString.split(',');
    return keywords.map((keyword) => keyword.trim());
  }
}
